/**
 * Campo de número de tarjeta
 * -----------------------------------------------------------------------------
 * Agrupa los dígitos mientras se escribe, reconoce la marca por el prefijo y
 * comprueba el dígito de control. Envía el número sin espacios.
 *
 *   <div class="form-outline" data-mdb-input-init data-company-card-init>
 *     <input type="text" class="form-control" id="tarjeta" name="tarjeta" />
 *     <label class="form-label" for="tarjeta">Número de tarjeta</label>
 *   </div>
 *
 * La agrupación NO es fija: American Express va en 4-6-5 y Diners en 4-6-4, no
 * en cuatro bloques de cuatro. Escribir un Amex en bloques de cuatro obliga a
 * leerlo a saltos para compararlo con el plástico.
 *
 * Igual que en el campo de moneda, el valor que se envía es el crudo: el input
 * visible lleva los espacios y un `<input type="hidden">` con el `name` original
 * lleva solo los dígitos.
 *
 * Sobre los datos: el theme **no guarda ni manda el número a ninguna parte**,
 * solo lo formatea en pantalla. Que el número llegue o no a tu servidor es
 * decisión de la aplicación, y recibirlo tiene consecuencias: quien almacena o
 * transmite números de tarjeta entra en el alcance de PCI DSS. Lo habitual es
 * que el campo lo lea la pasarela de pago y el servidor solo vea un token.
 */

import { cardES } from './locale-es.js';
import { syncLabel } from './floating-labels.js';

const ATTR = 'data-company-card-init';

/**
 * Marcas por prefijo. El ORDEN importa: se devuelve la primera que encaje, y
 * hay rangos que se solapan —Maestro empieza por 50 y Mastercard por 51-55—.
 *
 * `mask` son los tamaños de cada bloque; el último grupo puede quedar corto.
 * `lengths` son las longitudes válidas del número completo.
 */
const BRANDS = [
  {
    id: 'amex',
    label: 'American Express',
    test: /^3[47]/,
    mask: [4, 6, 5],
    lengths: [15],
    cvv: 4, // Amex lleva cuatro dígitos, no tres
    color: '#2E77BC',
  },
  {
    id: 'diners',
    label: 'Diners Club',
    test: /^3(?:0[0-5]|095|6|8|9)/,
    mask: [4, 6, 4],
    lengths: [14, 16, 19],
    cvv: 3,
    color: '#0079BE',
  },
  {
    id: 'jcb',
    label: 'JCB',
    test: /^35(?:2[89]|[3-8])/,
    mask: [4, 4, 4, 4],
    lengths: [16, 17, 18, 19],
    cvv: 3,
    color: '#0B4EA2',
  },
  {
    id: 'visa',
    label: 'Visa',
    test: /^4/,
    mask: [4, 4, 4, 4],
    lengths: [13, 16, 19],
    cvv: 3,
    color: '#1A1F71',
  },
  {
    id: 'mastercard',
    label: 'Mastercard',
    test: /^(5[1-5]|2(?:2[2-9]|[3-6]|7[01]|720))/,
    mask: [4, 4, 4, 4],
    lengths: [16],
    cvv: 3,
    color: '#EB001B',
  },
  {
    id: 'discover',
    label: 'Discover',
    test: /^(6011|64[4-9]|65)/,
    mask: [4, 4, 4, 4],
    lengths: [16, 19],
    cvv: 3,
    color: '#F76B1C',
  },
  {
    id: 'unionpay',
    label: 'UnionPay',
    test: /^62/,
    mask: [4, 4, 4, 4],
    lengths: [16, 17, 18, 19],
    cvv: 3,
    color: '#E21836',
  },
  {
    id: 'maestro',
    label: 'Maestro',
    test: /^(5[06-9]|6304|67)/,
    mask: [4, 4, 4, 4],
    lengths: [12, 13, 14, 15, 16, 17, 18, 19],
    cvv: 3,
    color: '#6C6BBD',
  },
];

/** Cuando no se reconoce el prefijo: bloques de cuatro y longitud abierta. */
const UNKNOWN = { id: null, label: null, mask: [4, 4, 4, 4], lengths: [], cvv: 3 };

function brandOf(digits) {
  if (!digits) return UNKNOWN;
  return BRANDS.find((brand) => brand.test.test(digits)) || UNKNOWN;
}

/**
 * Algoritmo de Luhn: la comprobación que traen los propios números de tarjeta.
 * Caza el 90 % de las erratas al teclear —un dígito mal, dos cambiados de
 * sitio— sin preguntarle nada al banco.
 */
function luhn(digits) {
  let sum = 0;
  let alternate = false;

  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = parseInt(digits.charAt(i), 10);

    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }

    sum += n;
    alternate = !alternate;
  }

  return sum % 10 === 0;
}

function group(digits, mask) {
  const parts = [];
  let rest = digits;

  mask.forEach((size) => {
    if (!rest) return;
    parts.push(rest.slice(0, size));
    rest = rest.slice(size);
  });

  // Lo que sobre de la máscara —tarjetas de 19 dígitos— sigue de cuatro en cuatro.
  while (rest) {
    parts.push(rest.slice(0, 4));
    rest = rest.slice(4);
  }

  return parts.join(' ');
}

class CardInput {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('input');
    if (!this.input) throw new Error('company-theme: campo de tarjeta sin <input>');

    this.name = this.input.getAttribute('name') || '';
    this.brand = UNKNOWN;

    // El input visible pierde el `name`: lo hereda el oculto, que es el que se
    // envía. Así el formulario manda `4111111111111111` y no `4111 1111 …`.
    if (this.name) {
      this.hidden = document.createElement('input');
      this.hidden.type = 'hidden';
      this.hidden.name = this.name;
      this.input.removeAttribute('name');
      this.input.id = this.input.id || `card-${this.name}`;
      wrapper.appendChild(this.hidden);
    }

    // Teclado numérico en el móvil sin usar `type="number"`, que quitaría el
    // formateo y traería las flechas de incremento.
    this.input.setAttribute('inputmode', 'numeric');
    this.input.setAttribute('autocomplete', 'cc-number');

    this._addBrandMark();

    this.input.addEventListener('input', () => this._format());
    this.input.addEventListener('blur', () => this._validate());

    if (this.input.value) this._format();

    wrapper.__companyCard = this;
  }

  /** Marca de la tarjeta, a la derecha del campo. */
  _addBrandMark() {
    this.mark = document.createElement('span');
    this.mark.className = 'company-card-brand';
    this.mark.hidden = true;
    this.wrapper.appendChild(this.mark);
    this.wrapper.classList.add('company-card');
  }

  _format() {
    const previous = this.input.value;
    const start = this.input.selectionStart;

    const digits = previous.replace(/\D/g, '').slice(0, 19);
    this.brand = brandOf(digits);

    const formatted = group(digits, this.brand.mask);
    this.input.value = formatted;
    syncLabel(this.input);
    if (this.hidden) this.hidden.value = digits;

    // Se recoloca el cursor contando DÍGITOS, no caracteres: al insertarse un
    // espacio delante, un cursor por posición saltaría un sitio hacia atrás y
    // escribir en medio del número sería imposible.
    if (start !== null && start < previous.length) {
      const digitsBefore = previous.slice(0, start).replace(/\D/g, '').length;
      let position = 0;
      let seen = 0;

      while (position < formatted.length && seen < digitsBefore) {
        if (/\d/.test(formatted.charAt(position))) seen += 1;
        position += 1;
      }

      this.input.setSelectionRange(position, position);
    }

    this._paintBrand();
    this.wrapper.classList.remove('is-invalid');
  }

  _paintBrand() {
    const brand = this.brand;

    this.mark.hidden = !brand.id;
    if (!brand.id) return;

    this.mark.dataset.brand = brand.id;
    this.mark.setAttribute('title', brand.label);
    this.mark.setAttribute('aria-label', brand.label);

    // Mastercard son sus dos círculos, que se dibujan exactos con dos nodos.
    // Las demás van como texto con su color: reproducir el logotipo oficial de
    // cada marca no aporta y traería 115 KB de tipografía de iconos.
    this.mark.innerHTML =
      brand.id === 'mastercard'
        ? '<span class="company-card-circles"><i></i><i></i></span>'
        : '';

    if (brand.id !== 'mastercard') this.mark.textContent = brand.label;
    this.mark.style.setProperty('--company-card-color', brand.color);
  }

  _validate() {
    const valid = this.isValid();
    this.wrapper.classList.toggle('is-invalid', !!this.value && !valid);
    return valid;
  }

  /** Dígitos sin espacios. */
  get value() {
    return this.input.value.replace(/\D/g, '');
  }

  /**
   * Si el número está completo y pasa el dígito de control.
   *
   * Vacío cuenta como válido: si el campo es obligatorio, eso lo dice
   * `required`, no este componente.
   */
  isValid() {
    const digits = this.value;
    if (!digits) return true;

    const lengths = this.brand.lengths;
    if (lengths.length && lengths.indexOf(digits.length) === -1) return false;
    if (!lengths.length && (digits.length < 12 || digits.length > 19)) return false;

    return luhn(digits);
  }

  /** Marca reconocida: `{ id, label, cvv }`, o `null` si aún no se sabe. */
  getBrand() {
    return this.brand.id ? { id: this.brand.id, label: this.brand.label, cvv: this.brand.cvv } : null;
  }

  setValue(value) {
    this.input.value = String(value || '');
    this._format();
  }

  dispose() {
    if (this.mark) this.mark.remove();
    if (this.hidden) {
      this.input.setAttribute('name', this.name);
      this.hidden.remove();
    }
    this.wrapper.classList.remove('company-card');
    delete this.wrapper.__companyCard;
  }
}

/**
 * @param {ParentNode} [root=document]
 */
export function setupCardInputs(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll(`[${ATTR}]`).forEach((wrapper) => {
    if (wrapper.__companyCard) return;
    new CardInput(wrapper);
  });
}

export { CardInput, luhn, brandOf, cardES };
export default setupCardInputs;
